import type { CreateFlow } from '@/utils/analytics-events';

/** Comic words, keyed by base language code. Picture-book flow only. */
export const COMIC_KEYWORDS: Record<string, readonly string[]> = {
  en: ['comic', 'manga', 'graphic novel', 'speech bubble', 'speech balloon', 'panels'],
  es: ['cómic', 'historieta'],
  fr: ['bande dessinée', 'bd'],
  de: ['comic'],
  it: ['fumetto', 'fumetti'],
  pt: ['quadrinhos', 'hq', 'banda desenhada'],
  nl: ['strip', 'stripboek'],
  pl: ['komiks'],
  ru: ['комикс'],
  tr: ['çizgi roman'],
  sv: ['serie', 'serietidning'],
  ja: ['漫画', 'マンガ', 'コミック'],
  ko: ['만화', '웹툰'],
  zh: ['漫画', '漫畫', '连环画'],
  ar: ['كوميك', 'قصة مصورة'],
  hi: ['कॉमिक'],
  mr: ['कॉमिक'],
  da: ['tegneserie'],
  no: ['tegneserie'],
  fi: ['sarjakuva'],
  he: ['קומיקס'],
};

/** Picture-book words (English only), comic flow. */
export const PICTURE_BOOK_KEYWORDS_EN: readonly string[] = [
  'picture book',
  'picture-book',
  'storybook',
  'story book',
  'bedtime story',
];

export function normalizeForMatch(s: string): string {
  return s.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase();
}

/** Keeps combining marks (needed for Indic scripts, where marks carry the word). */
function normalizeKeepMarks(s: string): string {
  return s.normalize('NFKD').toLowerCase();
}

const ALPHABETIC = /^[\p{Script=Latin}\p{Script=Cyrillic}\s-]+$/u;
const EXACT = new Set(['bd', 'hq']);

interface Matcher {
  regexes: RegExp[];
  substrings: string[];
}

function buildMatcher(keywords: readonly string[], english: boolean): Matcher {
  const regexes: RegExp[] = [];
  const substrings: string[] = [];
  for (const raw of keywords) {
    const kw = normalizeForMatch(raw);
    if (!ALPHABETIC.test(kw)) {
      substrings.push(normalizeKeepMarks(raw));
      continue;
    }
    const body = kw
      .split(/[\s-]+/)
      .filter(Boolean)
      .map((w) => w.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&'))
      .join('[\\s-]+');
    const tail = EXACT.has(kw) ? '' : english ? '(?:e?s)?' : '\\p{L}{0,3}';
    regexes.push(new RegExp(`(?<![\\p{L}\\p{N}])${body}${tail}(?![\\p{L}\\p{N}])`, 'u'));
  }
  return { regexes, substrings };
}

const cache = new Map<string, Matcher[]>();

function matchersFor(flow: CreateFlow, language?: string | null): Matcher[] {
  const base = (language ?? 'en').split('-')[0].toLowerCase();
  const key = flow === 'comic' ? 'comic' : `pb:${base}`;
  let m = cache.get(key);
  if (!m) {
    if (flow === 'comic') {
      m = [buildMatcher(PICTURE_BOOK_KEYWORDS_EN, true)];
    } else {
      m = [buildMatcher(COMIC_KEYWORDS.en, true)];
      if (base !== 'en' && COMIC_KEYWORDS[base]) m.push(buildMatcher(COMIC_KEYWORDS[base], false));
    }
    cache.set(key, m);
  }
  return m;
}

/** True when the text names the other format than the one this flow makes. */
export function formatMismatch(text: string, flow: CreateFlow, language?: string | null): boolean {
  const t = normalizeForMatch(text);
  if (!t.trim()) return false;
  const tk = normalizeKeepMarks(text);
  return matchersFor(flow, language).some(
    (m) => m.regexes.some((r) => r.test(t)) || m.substrings.some((s) => tk.includes(s)),
  );
}

export type MismatchPromptState = 'idle' | 'shown' | 'accepted' | 'dismissed';

export function nextPromptState(state: MismatchPromptState, matches: boolean): MismatchPromptState {
  return state === 'idle' && matches ? 'shown' : state;
}

export function promptVisible(state: MismatchPromptState, matches: boolean): boolean {
  return state === 'shown' && matches;
}

/** Text the parent wrote: non-empty fields that differ from every text we filled in. */
export function ownText(
  fields: ReadonlyArray<{ text: string; ours: ReadonlyArray<string | null | undefined> }>,
): string {
  const out: string[] = [];
  for (const f of fields) {
    const t = f.text.trim();
    if (!t) continue;
    if (f.ours.some((o) => o != null && o.trim() === t)) continue;
    out.push(t);
  }
  return out.join('\n');
}
